/**
 * SEO Landing Page for location + service combinations
 * e.g., /gardening-hobart, /cleaning-new-norfolk
 */

import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Phone, Mail, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ContactCTA } from '@/components/features/ContactCTA';
import { SITE_CONFIG } from '@/lib/constants';
import { useEffect } from 'react';

export interface LocationServiceConfig {
  slug: string;
  location: string;
  service: 'gardening' | 'cleaning' | 'taxidermy';
  title: string;
  metaDescription: string;
  heroTitle: string;
  heroSubtitle: string;
  intro: string;
  services: string[];
  benefits: string[];
  nearbyAreas: string[];
}

interface LocationServicePageProps {
  config: LocationServiceConfig;
}

const serviceLinks = {
  gardening: '/services/gardening',
  cleaning: '/services/cleaning',
  taxidermy: '/services/taxidermy',
};

const serviceNames = {
  gardening: 'Gardening',
  cleaning: 'Cleaning',
  taxidermy: 'Taxidermy',
};

export function LocationServicePage({ config }: LocationServicePageProps) {
  // Update document title for SEO
  useEffect(() => {
    document.title = config.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', config.metaDescription);
    }
  }, [config]);

  return (
    <>
      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container-custom">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-sm text-primary mb-4">
              <MapPin className="h-4 w-4" />
              <span>{config.location}, Tasmania</span>
            </div>
            <h1 className="text-4xl font-bold md:text-5xl">
              {config.heroTitle}
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              {config.heroSubtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild size="lg" className="gap-2">
                <Link to="/contact">
                  Get a Free Quote
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to={serviceLinks[config.service]}>
                  View {serviceNames[config.service]} Services
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="category-label">{serviceNames[config.service]} in {config.location}</span>
              <h2 className="text-3xl font-bold">
                Professional {serviceNames[config.service]} Services
              </h2>
              <p className="mt-4 text-muted-foreground">
                {config.intro}
              </p>
              <p className="mt-4 text-muted-foreground">
                Based in Lachlan in the Derwent Valley, Cloud9 Tasmania provides reliable
                {' '}{config.service} services throughout {config.location} and surrounding areas.
                With a commitment to quality and customer satisfaction, I deliver results you can trust.
              </p>
            </div>
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-4">Contact Us Today</h3>
                <div className="space-y-4">
                  <a
                    href={`mailto:${SITE_CONFIG.contact.email}`}
                    className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Mail className="h-5 w-5" />
                    {SITE_CONFIG.contact.email}
                  </a>
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <MapPin className="h-5 w-5" />
                    <span>Serving {config.location} & surrounding areas</span>
                  </div>
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Phone className="h-5 w-5" />
                    <span>24-48hr response time</span>
                  </div>
                </div>
                <Button asChild className="w-full mt-6">
                  <Link to="/contact">Request a Quote</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="category-label">Our Services</span>
            <h2 className="text-3xl font-bold">
              {serviceNames[config.service]} Services in {config.location}
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {config.services.map((service, index) => (
              <Card key={index} className="card-hover">
                <CardContent className="flex items-start gap-3 p-4">
                  <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <span>{service}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="category-label">Why Choose Us</span>
            <h2 className="text-3xl font-bold">
              Why Choose Cloud9 Tasmania in {config.location}
            </h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {config.benefits.map((benefit, index) => (
              <Card key={index} className="card-hover">
                <CardContent className="p-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary mb-4">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <p className="font-medium">{benefit}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby Areas */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold">
              Also Serving Nearby Areas
            </h2>
            <p className="mt-2 text-muted-foreground">
              In addition to {config.location}, we provide {config.service} services to:
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {config.nearbyAreas.map((area, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 rounded-full bg-muted px-4 py-2 text-sm"
              >
                <MapPin className="h-3 w-3 text-primary" />
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <ContactCTA />
    </>
  );
}
